import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/superintendencia.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const SuperintendenciaManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_superintendencia: '',
    nome_super: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/superintendencia');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
  }, [verificarAPI]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'superintendência', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/superintendencia/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'superintendência', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'superintendência');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'superintendência', { id });
    try {
      const response = await api.delete(`/superintendencia/${id}`);
      logarSucesso('Excluir', 'superintendência', response.data);
      const novosDados = dados.filter(item => item.id_superintendencia !== id);
      setDados(novosDados);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'superintendência');
      mostrarMensagem('error', mensagemErro);
    }
  }, [dados, mostrarMensagem]);

  const colunas = useMemo(() => [
    {
      title: 'Salvar',
      field: 'salvar',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        atualizarRegistro(data.id_superintendencia, data);
      },
      headerSort: false
    },
    {
      title: 'Excluir',
      field: 'excluir',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        if (window.confirm(`Deseja realmente excluir o registro "${data.id_superintendencia}"?`)) {
          excluirRegistro(data.id_superintendencia);
        }
      },
      headerSort: false
    },
    {
      title: 'ID',
      field: 'id_superintendencia',
      width: 200,
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar ID...',
      sorter: 'string'
    },
    {
      title: 'Nome da Superintendência',
      field: 'nome_super',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar nome...',
      sorter: 'string',
      editor: 'input'
    }
  ], [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/superintendencia');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_superintendencia'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_superintendencia, nome_super } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_superintendencia) {
      camposObrigatorios.push('ID');
      erros.push('id_superintendencia');
    }
    if (!nome_super) {
      camposObrigatorios.push('Nome da Superintendência');
      erros.push('nome_super');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_superintendencia,
      nome_super
    };

    logarRequisicao('Adicionar', 'superintendência', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/superintendencia', dataToSend);
      logarSucesso('Adicionar', 'superintendência', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_superintendencia: '', nome_super: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'superintendência');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'superintendencias.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Superintendências</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_superintendencia</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/superintendencia.mov" buttonText="Ver Tutorial Superintendência" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_superintendencia">ID:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_superintendencia" name="id_superintendencia" value={formData.id_superintendencia} onChange={handleInputChange} placeholder="Ex: SUP_NORTE" maxLength={255} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_superintendencia') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="nome_super">Nome da superintendência:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="nome_super" name="nome_super" value={formData.nome_super} onChange={handleInputChange} placeholder="Ex: Superintendência Norte" maxLength={50} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('nome_super') ? 'input-error' : ''} />
          </div>
          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>

            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default SuperintendenciaManager;